import { use, useEffect, useMemo } from 'react';
import { useParams, usePath, useQuery } from 'crossroad';
import { TranslationDataContext } from '@/data/translation/translation-data-context';
import { ReadingView } from './reading-view';
import { ReadingViewSkeleton } from './reading-view-skeleton';
import { getInitialScrollIndex, getReadingRedirectUrl } from '@/lib/utils/scroll-position';
import { useActivityLogger } from '@/lib/activity-logger';

const EMPTY_PARAGRAPHS: TranslationParagraph[] = [];

/**
 * Wrapper component that handles URL redirects for /app/read
 * If no book/chapter params, redirects to localStorage position or Genesis 1
 * Otherwise, calculates initialScrollIndex and passes to ReadingView
 */
export function ReadingViewWrapper() {
    const { data: paragraphsData } = use(TranslationDataContext);
    const [, setPath] = usePath();
    const [query] = useQuery();
    const params = useParams('/app/read/:book/:chapter');
    const activityLogger = useActivityLogger();

    // Log reading view with a delay to avoid spam during rapid scrolling
    useEffect(() => {
        if (params?.book && params?.chapter) {
            const timer = setTimeout(() => {
                activityLogger.log('reading_view', `Browsing ${params.book} ${params.chapter}`, {
                    book: params.book,
                    chapter: params.chapter
                });
            }, 2000); // Only log if they stay on the chapter for 2 seconds

            return () => clearTimeout(timer);
        }
    }, [params?.book, params?.chapter, activityLogger]);

    // Calculate initial scroll position from URL params
    // Note: Only calculate on initial mount when book/chapter changes, not when verse query param changes
    // The verse tracking system will handle scroll position updates after initial load
    const initialScrollIndex = useMemo(() => {
        if (!paragraphsData) return 0;
        return getInitialScrollIndex(paragraphsData, params, query.verse);
    }, [paragraphsData, params?.book, params?.chapter, query.verse]);

    const paragraphs = useMemo(() => {
        return paragraphsData?.items[0]?.paragraphs || EMPTY_PARAGRAPHS;
    }, [paragraphsData]);

    const version = useMemo(() => {
        return paragraphsData?.items[0]?.version || 'csb';
    }, [paragraphsData]);

    // If on /app/read (no book/chapter params), redirect to saved position or Genesis 1
    useEffect(() => {
        if (!params?.book && paragraphsData) {
            const redirectUrl = getReadingRedirectUrl();
            setPath(redirectUrl);
        }
    }, [params?.book, paragraphsData, setPath]);

    // Don't render until we have data and URL params (redirect will happen)
    if (!paragraphsData || !params?.book || !paragraphsData.items || paragraphsData.items.length === 0) {
        return <ReadingViewSkeleton />;
    }

    return <ReadingView paragraphs={paragraphs} version={version} initialScrollIndex={initialScrollIndex} />;
}

